<template>
  <div class="h-safe">
    <div
      class="layout p-safe"
      :class="{
        _collapsed: collapsed,
        _collapsible: collapsed && !isMobile,
        '_aside-hidden': isAsideHidden,
        _wide: wide
      }">
      <aside v-if="$slots.aside" id="layout_aside" class="layout__aside">
        <header class="layout__aside-header">
          <nuxt-link :to="{ name: 'index' }">
            <b-logo-small />
          </nuxt-link>

          <Button
            icon="pi pi-ellipsis-h"
            text
            rounded
            raised
            aria-haspopup="true"
            aria-controls="app_actions"
            @click="toggleAppMenu" />

          <Menu id="app_actions" ref="appMenu" :model="appActions" popup>
            <template #item="{ label, item, props }">
              <a v-bind="props.action" :href="item.url" :target="item.target">
                <span v-if="item.icon" v-bind="props.icon" />
                <span v-if="item.subtitle" class="flex flex-col">
                  <span v-bind="props.label">{{ label }}</span>
                  <span class="text-xs text-black/40">{{ item.subtitle }}</span>
                </span>
                <span v-else v-bind="props.label">{{ label }}</span>
              </a>
            </template>
          </Menu>
        </header>

        <h1 class="layout__aside-title">
          {{
            $t('app.title', {
              salutation: userStore.user?.salutation ? $t(`app.salutation.${userStore.user?.salutation}`) : '',
              name: `${userStore.user?.first_name} ${userStore.user?.last_name}`
            })
          }}
        </h1>

        <div v-if="!isNativePlatform" class="layout__aside-template-manager">
          <p>{{ $t('app.template-manager.disclaimer') }}</p>

          <nuxt-link :to="{ name: 'template-manager' }" class="flex-shrink-0">
            <Button :label="$t('app.template-manager.link-button')" rounded outlined size="small" />
          </nuxt-link>
        </div>

        <div class="layout__aside-inner">
          <slot name="aside" />
        </div>
      </aside>

      <template v-if="collapsed && !isMobile && $slots.aside">
        <b-transition>
          <nuxt-link v-if="isAsideHidden" :to="{ name: 'index' }" class="layout__logo">
            <b-logo-small />
          </nuxt-link>
        </b-transition>

        <button
          type="button"
          class="layout__aside-toggle"
          :aria-expanded="!isAsideHidden"
          aria-controls="layout_aside"
          :aria-label="isAsideHidden ? $t('app.show-list-button') : $t('app.hide-list-button')"
          @click="isAsideHidden = !isAsideHidden">
          <i :class="isAsideHidden ? 'pi pi-chevron-right' : 'pi pi-chevron-left'" />
        </button>
      </template>

      <main v-if="$slots.default" class="layout__content">
        <slot />
      </main>
    </div>

    <modal-manager />

    <b-upload-indicator />

    <Toast position="bottom-right" group="br" />
  </div>
</template>

<script setup lang="ts">
import sortBy from 'lodash/sortBy';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { PrimeIcons } from 'primevue/api';
import { Locales, supportEmail, tutorialVideosUrl, tutorialVideosUrlByLocale } from '~/utils/constants/common';

defineProps<{
  collapsed?: boolean;
  // Wider content column, e.g. for reports
  wide?: boolean;
}>();

const { t, locale: currentLocale, availableLocales } = useI18n();

const { isNativePlatform } = usePlatform();

const { isMobile } = useScreens();

// The list next to the content can be hidden on desktop, it's shown again whenever the layout is mounted
const isAsideHidden = ref(false);

const { primevue, primevueLocales, updateUserLanguage, setLocale } = useLocale();

const authStore = useAuthStore();

const userStore = useUserStore();

const { updatePassword, updateDignities } = useManageUser();

onBeforeMount(async () => {
  const localeToSet = primevueLocales[currentLocale.value as Locales];

  if (primevue.config.locale?.startsWith !== localeToSet?.startsWith) {
    primevue.config.locale = localeToSet;
  }

  if (authStore.isLoggedIn && userStore.user?.settings.language !== currentLocale.value) {
    await updateUserLanguage(currentLocale.value as Locales);
  }

  // Set safe area insets
  const { insets } = await SafeArea.getSafeAreaInsets();

  Object.entries(insets).forEach(([key, value]) => {
    document.documentElement.style.setProperty(`--safe-area-inset-${key}`, `${value}px`);
  });
});

onMounted(() => {
  // If it's the first login for the user, the user has to change the password
  if (authStore.isLoggedIn && userStore.user?.is_password_reset_required) {
    updatePassword();
  }
});

const appMenu = ref();

const toggleAppMenu = (event: MouseEvent) => {
  appMenu.value.toggle(event);
};

const appActions = computed(() => {
  const _actions = [];

  const sortedLocales = sortBy(availableLocales, (locale) => Object.values(Locales).indexOf(locale as Locales));

  _actions.push({
    label: t('app.menu.language-title'),
    items: sortedLocales.map((locale) => ({
      label: locale.toUpperCase(),
      icon: locale === currentLocale.value ? PrimeIcons.CHECK : undefined,
      command: () => {
        setLocale(locale as Locales);
      }
    }))
  });

  if (!isNativePlatform) {
    _actions.push(
      ...[
        {
          label: t('app.menu.profile-title'),
          items: [
            {
              label: t('app.menu.memory-button'),
              command: () => navigateTo({ name: 'memory' })
            },
            {
              label: t('app.menu.manage-dignities-button'),
              command: updateDignities
            },
            {
              label: t('app.menu.change-password-button'),
              command: updatePassword
            }
          ]
        }
      ]
    );
  }

  _actions.push({
    label: t('app.menu.help-title'),
    items: [
      {
        label: t('app.menu.support-button'),
        icon: PrimeIcons.ENVELOPE,
        subtitle: supportEmail,
        url: `mailto:${supportEmail}`,
        target: '_blank'
      },
      {
        label: t('app.menu.tutorial-videos-button'),
        icon: PrimeIcons.VIDEO,
        url: tutorialVideosUrlByLocale[currentLocale.value as Locales] ?? tutorialVideosUrl,
        target: '_blank'
      }
    ]
  });

  _actions.push({
    label: t('app.menu.logout-button'),
    icon: PrimeIcons.SIGN_OUT,
    command: authStore.logout
  });

  return _actions;
});
</script>

<style lang="postcss">
.layout {
  @apply relative flex h-screen w-screen justify-center overflow-hidden;

  /* Sidebar shrinks on smaller screens */
  --aside-width: clamp(320px, 30vw, 440px);

  &._collapsed {
    @apply justify-normal;

    .layout {
      &__aside {
        @apply max-w-[var(--aside-width)] border-r border-black/10;
      }
    }
  }

  &._collapsible {
    .layout__aside {
      @apply w-[var(--aside-width)];
      transition:
        width 200ms cubic-bezier(0.4, 0, 0.2, 1),
        padding 200ms cubic-bezier(0.4, 0, 0.2, 1);

      & > * {
        @apply w-[calc(var(--aside-width)-64px)];
      }
    }

    &._aside-hidden .layout__aside {
      @apply w-0 overflow-hidden border-r-0 px-0;
    }
  }

  &__logo {
    @apply absolute left-8 top-4 z-10;

    &.fade-enter-active {
      @apply delay-[120ms] duration-[160ms];
    }

    &.fade-leave-active {
      @apply duration-0;
    }
  }

  &__aside-toggle {
    @apply absolute left-[var(--aside-width)] top-1/2 z-10 flex h-14 w-5 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-black/10 bg-white text-primary-500 shadow-[0_1px_2px_rgba(0,0,0,0.06)] hover:bg-gray-50;
    transition:
      left 200ms cubic-bezier(0.4, 0, 0.2, 1),
      transform 200ms cubic-bezier(0.4, 0, 0.2, 1),
      background-color 150ms;

    .pi {
      @apply text-[12px];
    }

    ._aside-hidden & {
      @apply left-0 translate-x-0 rounded-l-none rounded-r-[10px];
    }
  }

  &__aside {
    @apply flex w-full max-w-[720px] flex-shrink-0 flex-col overflow-y-auto px-8;

    &-header {
      @apply mb-6 mt-4 flex items-center justify-between gap-4;
    }

    &-title {
      @apply mb-4;
    }

    &-template-manager {
      @apply mb-4 flex items-center justify-between gap-4 rounded-md border border-secondary-300 p-3;
    }

    &-inner {
      @apply flex flex-shrink flex-grow overflow-y-auto;

      & > * {
        @apply w-full;
      }
    }
  }

  &__content {
    @apply mx-auto flex w-full min-w-0 max-w-[800px] flex-grow px-8 pb-8 pt-4;

    & > * {
      @apply w-full min-w-0;
    }
  }

  &._wide &__content {
    /* Grows with the window, but stays within a readable line length */
    @apply max-w-[clamp(800px,55vw,960px)];
  }
}
</style>
